/**
 * Product Description Accordion - Variant Template Only
 *
 * Transforms purple span headings inside description tab into collapsible accordions
 * Falls back gracefully on any errors
 */

(function() {
    'use strict';
  
    // Configuration
    const CONFIG = {
      descriptionTabSelectors: ['#tab-1', '#tab-4'], // Description and Descripción en Español
      transitionDuration: 300, // ms
      maxRetries: 3,
      retryDelay: 100 // ms
    };
  
    // State
    let initialized = false;
  
    /**
     * Find description tabs by trying hardcoded IDs + text matching
     */
    function findDescriptionTabs() {
      const tabIds = [];
  
      // Step 1: Try hardcoded selectors
      CONFIG.descriptionTabSelectors.forEach(selector => {
        if (document.querySelector(selector)) {
          tabIds.push(selector);
        }
      });
  
      // Step 2: ALSO search by text content (always run this)
      const links = document.querySelectorAll('a[href^="#tab-"]');
  
      links.forEach(link => {
        const text = link.textContent.toLowerCase().trim();
        // Match "description" or "descripción en español"
        if (text.includes('description') || text.includes('descripcion') || text.includes('descripción')) {
          const href = link.getAttribute('href');
          // Only add if not already in array
          if (href && !tabIds.includes(href)) {
            tabIds.push(href);
          }
        }
      });
  
      console.info('[PDP Accordion] Found description tabs:', tabIds);
      return tabIds;
    }
  
    /**
     * Main initialization function
     */
    function init() {
      if (initialized) return;
  
      try {
        let processedCount = 0;
  
        // Find all description tabs (hardcoded IDs + text matching)
        const tabSelectors = findDescriptionTabs();
  
        if (tabSelectors.length === 0) {
          console.info('[PDP Accordion] No description tabs found');
          return;
        }
  
        // Process each tab that needs accordion treatment
        tabSelectors.forEach(selector => {
          const tab = document.querySelector(selector);
  
          if (!tab) {
            console.info('[PDP Accordion] Tab not found:', selector);
            return;
          }
  
          // Find all purple/magenta span headings
          const headings = findPurpleHeadings(tab);
  
          if (!headings || headings.length === 0) {
            console.info('[PDP Accordion] No purple headings found in', selector);
            return;
          }
  
          // Transform headings into accordions
          transformHeadingsToAccordions(tab, headings);
          processedCount += headings.length;
        });
  
        if (processedCount > 0) {
          initialized = true;
          console.info('[PDP Accordion] Successfully initialized with', processedCount, 'accordion items');
        }
  
      } catch (error) {
        console.error('[PDP Accordion] Initialization failed, preserving original content:', error);
        // Do nothing - original content remains visible
      }
    }
  
    /**
     * Find all span elements with purple/magenta color
     */
    function findPurpleHeadings(container) {
      const headings = [];
      const allSpans = container.querySelectorAll('span[style*="color"]');
  
      allSpans.forEach(span => {
        const style = span.getAttribute('style') || '';
        const colorMatch = style.match(/color:\s*#?([0-9a-f]{3,6}|rgb\([^)]+\)|#c332c3)/i);
  
        if (colorMatch) {
          const color = colorMatch[0].toLowerCase();
          // Check if it's a purple/magenta color (#c332c3 or similar)
          if (color.includes('c332c3') || color.includes('c3') || color.includes('ce00ce') || color.includes('922692')) {
            headings.push(span);
          }
        }
      });
  
      return headings;
    }
  
    /**
     * Transform headings and their content into accordion sections
     */
    function transformHeadingsToAccordions(container, headings) {
      const accordionWrapper = document.createElement('div');
      accordionWrapper.className = 'pdp-accordion';
      accordionWrapper.setAttribute('data-pdp-accordion', '');
  
      headings.forEach((heading, index) => {
        // Get heading HTML (preserves product-title spans)
        const headingText = heading.innerHTML.trim();
  
        // Collect content until next heading
        const content = collectContentUntilNextHeading(heading, headings[index + 1]);
  
        if (!content || content.length === 0) {
          return;
        }
  
        // Create accordion item
        const accordionItem = createAccordionItem(headingText, content, index);
        accordionWrapper.appendChild(accordionItem);
  
        // Remove original heading
        heading.parentElement.remove();
      });
  
      // Insert accordion at the beginning of the tab
      container.insertBefore(accordionWrapper, container.firstChild);
  
      // Attach event listeners
      attachAccordionListeners(accordionWrapper);
    }
  
    /**
     * Collect all elements between this heading and the next
     */
    function collectContentUntilNextHeading(currentHeading, nextHeading) {
      const content = [];
  
      // Check if we're in a .faq-item structure
      const faqItem = currentHeading.closest('.faq-item');
  
      if (faqItem) {
        // New format: collect all children of faq-item except the question paragraph
        const children = Array.from(faqItem.childNodes);
        const questionParagraph = currentHeading.parentElement;
  
        children.forEach(child => {
          // Skip the question paragraph and non-element nodes at start
          if (child === questionParagraph) return;
          if (child.nodeType === Node.TEXT_NODE && !child.textContent.trim()) return;
  
          // Clone and collect everything else
          content.push(child.cloneNode(true));
        });
  
        // Remove the now-processed faq-item
        faqItem.remove();
      } else {
        // Old format: use original logic
        let currentElement = currentHeading.parentElement.nextElementSibling;
        const stopElement = nextHeading ? nextHeading.parentElement : null;
  
        while (currentElement && currentElement !== stopElement) {
          const nextSibling = currentElement.nextElementSibling;
          content.push(currentElement.cloneNode(true));
          currentElement.remove();
          currentElement = nextSibling;
        }
      }
  
      return content;
    }
  
    /**
     * Create accordion item HTML element
     */
    function createAccordionItem(title, contentElements, index) {
      const item = document.createElement('div');
      item.className = 'pdp-accordion--item' + (index === 0 ? ' pdp-accordion--item-open' : '');
      item.setAttribute('data-accordion-item', '');
  
      const trigger = document.createElement('button');
      trigger.className = 'pdp-accordion--trigger';
      trigger.setAttribute('data-accordion-trigger', '');
      trigger.setAttribute('aria-expanded', index === 0 ? 'true' : 'false');
      trigger.setAttribute('type', 'button');
  
      const titleSpan = document.createElement('span');
      titleSpan.className = 'pdp-accordion--title';
      titleSpan.innerHTML = title;
  
      const iconSpan = document.createElement('span');
      iconSpan.className = 'pdp-accordion--icon';
      iconSpan.setAttribute('aria-hidden', 'true');
      iconSpan.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      `;
  
      trigger.appendChild(titleSpan);
      trigger.appendChild(iconSpan);
  
      const contentDiv = document.createElement('div');
      contentDiv.className = 'pdp-accordion--content';
      contentDiv.setAttribute('data-accordion-content', '');
      if (index !== 0) {
        contentDiv.setAttribute('hidden', '');
      }
  
      const contentInner = document.createElement('div');
      contentInner.className = 'pdp-accordion--content-inner';
  
      contentElements.forEach(el => {
        contentInner.appendChild(el);
      });
  
      contentDiv.appendChild(contentInner);
  
      item.appendChild(trigger);
      item.appendChild(contentDiv);
  
      return item;
    }
  
    /**
     * Attach click event listeners to accordion triggers
     */
    function attachAccordionListeners(container) {
      const triggers = container.querySelectorAll('[data-accordion-trigger]');
  
      triggers.forEach(trigger => {
        trigger.addEventListener('click', handleAccordionClick);
      });
    }
  
    /**
     * Handle accordion item click
     */
    function handleAccordionClick(event) {
      try {
        const trigger = event.currentTarget;
        const item = trigger.closest('[data-accordion-item]');
        const content = item.querySelector('[data-accordion-content]');
        const isOpen = item.classList.contains('pdp-accordion--item-open');
  
        if (isOpen) {
          // Close
          item.classList.remove('pdp-accordion--item-open');
          trigger.setAttribute('aria-expanded', 'false');
          slideUp(content);
        } else {
          // Open
          item.classList.add('pdp-accordion--item-open');
          trigger.setAttribute('aria-expanded', 'true');
          slideDown(content);
        }
  
      } catch (error) {
        console.error('[PDP Accordion] Click handler error:', error);
      }
    }
  
    /**
     * Slide down animation
     */
    function slideDown(element) {
      element.removeAttribute('hidden');
      element.style.overflow = 'hidden';
      element.style.height = '0';
      element.style.transition = `height ${CONFIG.transitionDuration}ms ease`;
  
      requestAnimationFrame(() => {
        element.style.height = element.scrollHeight + 'px';
  
        setTimeout(() => {
          element.style.height = '';
          element.style.overflow = '';
          element.style.transition = '';
        }, CONFIG.transitionDuration);
      });
    }
  
    /**
     * Slide up animation
     */
    function slideUp(element) {
      element.style.overflow = 'hidden';
      element.style.height = element.scrollHeight + 'px';
      element.style.transition = `height ${CONFIG.transitionDuration}ms ease`;
  
      requestAnimationFrame(() => {
        element.style.height = '0';
  
        setTimeout(() => {
          element.setAttribute('hidden', '');
          element.style.height = '';
          element.style.overflow = '';
          element.style.transition = '';
        }, CONFIG.transitionDuration);
      });
    }
  
    /**
     * Initialize with retry mechanism
     */
    function initWithRetry(retries = 0) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initWithRetry(retries));
        return;
      }
  
      if (retries >= CONFIG.maxRetries) {
        console.warn('[PDP Accordion] Max retries reached');
        return;
      }
  
      // Check if at least one tab exists
      const hasTab = CONFIG.descriptionTabSelectors.some(selector =>
        document.querySelector(selector) !== null
      );
  
      if (!hasTab && retries < CONFIG.maxRetries) {
        setTimeout(() => initWithRetry(retries + 1), CONFIG.retryDelay);
        return;
      }
  
      init();
    }
  
    // Start initialization
    initWithRetry();
  
  })();